/**
 * Check if the origin is a known and trusted login origin
 * @param {String} origin 
 * @returns Boolean
 */
export function isTrustedLoginOrigin(origin) {
  const trustedOriginHosts = [
    "https://login.amiratutor.com",
    "https://sso.us-east-1.rosterstream.com",
    "https://app.istation.com",
    "https://home.app.amiralearning.com",
    "https://home.ca.amiralearning.com",
    "https://app-dev.istation.com",
    "https://api.cert.eng.hmhco.com",
    "https://api.eng.hmhco.com",
    "https://eng.hmhco.com",
    "https://hmhco.com",
    "https://cert.eng.hmhco.com",
    "https://cert.hmhco.com/ui",
    "https://cert.hmhco.com",
  ];

  const url = new URL(origin);
  return trustedOriginHosts.includes(url.origin);
}

/**
 * Side-effect-free mirror of redirectToLoginOrigin's decision: returns whether a call with the
 * same arguments would actually navigate (session ended page or a trusted redirect). Lets callers
 * detect the "no usable redirect" case synchronously without triggering the navigation.
 * @param {URLSearchParams} urlParams
 * @param {Boolean} shouldGoToSessionEndedPage
 * @returns {Boolean}
 */
export function wouldRedirectToLoginOrigin(urlParams, shouldGoToSessionEndedPage = false) {
  if (urlParams && (urlParams.get("login_origin") || urlParams.get("inboxReturnUrl"))) {
    return false;
  }
  if (shouldGoToSessionEndedPage) {
    return true;
  }
  try {
    const loginOrigin = localStorage.getItem("loginOrigin");
    return loginOrigin !== null && loginOrigin !== undefined && isTrustedLoginOrigin(loginOrigin);
  } catch (e) {
    console.error("Error checking login origin:", e);
    return false;
  }
}

/**
 * Redirect to the login origin if it is a trusted origin, clears the login origin from local storage and redirects.
 * @param {URLSearchParams} urlParams - used in the refresh to logout only
 * @param {Boolean} shouldGoToSessionEndedPage - used in the logout button only
 */
export function redirectToLoginOrigin(urlParams, shouldGoToSessionEndedPage = false) {
  // wouldRedirectToLoginOrigin owns the "should we navigate" decision so this acting function and
  // its side-effect-free mirror can never drift. Here we only carry out the resulting navigation.
  if (!wouldRedirectToLoginOrigin(urlParams, shouldGoToSessionEndedPage)) {
    return;
  }

  try {
    if (shouldGoToSessionEndedPage) {
      window.location.href = "/sessionEnded.html";
    } else {
      const loginOrigin = localStorage.getItem("loginOrigin");
      localStorage.removeItem("loginOrigin");
      window.location.href = loginOrigin;
    }
  } catch (e) {
    console.error("Error redirecting to login origin:", e);
  }
}

/**
 * Save the login origin to local storage so we can redirect back to it after refresh
 */
export function saveLoginOrigin(urlParams, loginOriginFallback) {
  const loginOrigin = urlParams.get("login_origin");
  if (loginOrigin || loginOriginFallback) {
    localStorage.setItem("loginOrigin", loginOrigin || loginOriginFallback);
  }
}
